import type { EquipmentOrigin, PostPaymentModelBody, ReceiptModelEnum } from '~/utils/receipt-model/ReceiptModel'
import { InvoiceType, PaymentModelStatus } from '~/constants/receiptModel'

// Pure rules of the receipt model step (no Vue, no i18n), shared by the step and its blocks so the same rule
// is not repeated in several places.

/** Invoice type of the GET (`receipt_model`), or `''` while there is no data. */
export type ReceiptModelValue = ReceiptModelEnum | ''

export interface EquipmentFlowInput {
  equipmentOrigin: EquipmentOrigin
  hasCombo: boolean
}

/**
 * Store order layout (read-only model, combo equipment summary, commission invoice only): order mirrored from the
 * store (`mirror`, even without `has_combo`) or combo that is not the container balance.
 */
export function isComboStoreOrder({ equipmentOrigin, hasCombo }: EquipmentFlowInput): boolean {
  return equipmentOrigin === 'mirror' || (hasCombo && equipmentOrigin !== 'container')
}

/** Supplier block (distributor, kit value, billing letter): separate invoices outside the mirrored store order. */
export function requiresSupplierSection(equipmentOrigin: EquipmentOrigin, receiptModel: ReceiptModelValue): boolean {
  return equipmentOrigin !== 'mirror' && receiptModel === InvoiceType.SEPARATE_INVOICES
}

/**
 * Step statuses that lock the fields (along with the terminal financing statuses, in `usePaymentModelAccess`).
 * The typo `awaiting_analisys_equipment` is kept on purpose: it never matches a real status, so
 * `awaiting_invoice_equipment` keeps the fields editable. Fixing it would lock the equipment invoice while the
 * billing letter is under analysis (rule change, needs a decision).
 */
const FIELD_LOCKING_STEP_STATUSES: ReadonlySet<string> = new Set<string>([
  PaymentModelStatus.APPROVED,
  PaymentModelStatus.REPROVED,
  PaymentModelStatus.UNDER_ANALYSIS,
  'awaiting_analisys_equipment',
])

export function isFieldLockingStepStatus(status: string | null | undefined): boolean {
  return !!status && FIELD_LOCKING_STEP_STATUSES.has(status)
}

export interface EquipmentValueInput extends EquipmentFlowInput {
  receiptModel: ReceiptModelValue
  /** Kit value of the supplier form, falling back to the GET. */
  kitValue: number
}

/**
 * `equipment_value` of the POST, in this order: combo (container included) → `null`; single invoice outside the
 * mirrored order → `null`; otherwise the kit value.
 */
export function getEquipmentValueToSend({ hasCombo, equipmentOrigin, receiptModel, kitValue }: EquipmentValueInput): number | null {
  if (hasCombo)
    return null

  if (equipmentOrigin !== 'mirror' && receiptModel === InvoiceType.SINGLE_INVOICE)
    return null

  return kitValue
}

export interface PostPaymentModelBodyInput {
  projectId: string
  payoutOption: string
  /** `hardware_supplier.value` of the form: `0` when no distributor was selected (initial value). */
  supplierId: number | null
  equipmentValue: number | null
  equipmentOrigin: EquipmentOrigin
}

/** Body of `POST /receipt-model`. No `receipt_model` (decided by the backend) and no `supplier_email` (sent by the letter PUT). */
export function buildPostPaymentModelBody(input: PostPaymentModelBodyInput): PostPaymentModelBody {
  return {
    project_id: input.projectId,
    issuance_model: 'installment',
    payout_option: input.payoutOption,
    supplier_id: input.supplierId,
    equipment_value: input.equipmentValue,
    equipment_origin: input.equipmentOrigin,
    shopping_cart_id: null,
  }
}

function readProperty(source: unknown, key: string): unknown {
  return typeof source === 'object' && source !== null
    ? (source as Record<string, unknown>)[key]
    : undefined
}

/**
 * `reason_code` of a failed POST: `detail.reason_code` (axios `response.data`, or `data` directly), falling back to
 * `response.data.reason_code`. `undefined` when there is none.
 */
export function getReceiptModelErrorReasonCode(error: unknown): string | undefined {
  const responseData = readProperty(readProperty(error, 'response'), 'data')
  const detail = readProperty(responseData, 'detail') ?? readProperty(readProperty(error, 'data'), 'detail')
  const reasonCode = readProperty(detail, 'reason_code') ?? readProperty(responseData, 'reason_code')

  return typeof reasonCode === 'string' ? reasonCode : undefined
}
